import React, { useState, useEffect } from 'react';
import { Table, Card, Space, Tag, Typography, Input, Button, message, Modal, Form, Select } from 'antd';
import { MailOutlined, SearchOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  border: 1px solid ${COLORS.border};
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const MerchantInternalEmails: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [filters, setFilters] = useState({
    recipientEmail: '',
  });
  const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm] = Form.useForm();
  const [loadingMerchants, setLoadingMerchants] = useState(false);

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    fetchEmails();
    fetchMerchants();
  }, [pagination.current, pagination.pageSize]);

  const fetchMerchants = async () => {
    setLoadingMerchants(true);
    try {
      // Fetch list of merchants to populate the recipient dropdown
      const response = await apiClient.get('/api/Administrator/users?userType=Merchant&perPage=1000');
      if (response.data?.status === 'success' && response.data?.data) {
        const users = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data.users || [];
        setMerchants(users.filter((u: any) => u.status === 'ACTIVE'));
      }
    } catch (error: any) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoadingMerchants(false);
    }
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        perPage: pagination.pageSize,
        type: 'INTERNAL_MESSAGE', // Only show internal messages, not birthday emails
      };

      if (filters.recipientEmail) {
        params.recipientEmail = filters.recipientEmail;
      }

      // Merchants can only see emails they sent or received
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_INTERNAL_EMAILS, { params });
      if (response.data?.status === 'success' && response.data?.data) {
        // Filter to only show emails where current merchant is sender or recipient
        const allEmails = response.data.data.emails || [];
        // Note: Backend should filter this, but we'll also filter on frontend for safety
        setEmails(allEmails);
        setTotal(response.data.data.pagination?.total || 0);
      }
    } catch (error: any) {
      message.error('Failed to fetch internal emails');
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchEmails();
  };

  const handleReset = () => {
    setFilters({
      recipientEmail: '',
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => fetchEmails(), 100);
  };

  const handleSendEmail = async (values: any) => {
    setSending(true);
    try {
      await apiClient.post(API_ENDPOINTS.MERCHANT_SEND_INTERNAL_EMAIL_TO_MERCHANT, {
        recipientEmail: values.recipientEmail,
        subject: values.subject,
        message: values.message,
      });
      message.success('Email sent successfully');
      setSendEmailModalVisible(false);
      sendForm.resetFields();
      fetchEmails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      title: 'Recipient',
      dataIndex: 'recipientEmail',
      key: 'recipientEmail',
      render: (email: string, record: any) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{email}</div>
          {record.recipientUserType && (
            <Tag color="green" style={{ marginTop: 4 }}>
              {record.recipientUserType}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Sender',
      dataIndex: 'senderEmail',
      key: 'senderEmail',
      render: (email: string, record: any) => email ? (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{email}</div>
          {record.senderUserType && (
            <Tag color={record.senderUserType === 'Administrator' ? 'blue' : 'green'} style={{ marginTop: 4 }}>
              {record.senderUserType}
            </Tag>
          )}
        </div>
      ) : <Text type="secondary">System</Text>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Sent',
      dataIndex: 'emailSent',
      key: 'emailSent',
      render: (sent: boolean) => (
        <Tag color={sent ? 'green' : 'red'}>{sent ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'emailSentAt',
      key: 'emailSentAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          <MailOutlined /> Internal Emails
        </Title>
        <Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setSendEmailModalVisible(true)}
          >
            Send Email to Merchant
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchEmails}>
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard>
        <FilterContainer>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Input
                placeholder="Filter by recipient email"
                value={filters.recipientEmail}
                onChange={(e) => setFilters({ ...filters, recipientEmail: e.target.value })}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
                onPressEnter={handleSearch}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
              <Button onClick={handleReset}>
                Reset
              </Button>
            </Space>
          </Space>
        </FilterContainer>

        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={emails}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: window.innerWidth > 768,
              showQuickJumper: window.innerWidth > 768,
              showTotal: (total) => `Total ${total} emails`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </StyledCard>

      {/* Send Email Modal */}
      <Modal
        title="Send Email to Merchant"
        open={sendEmailModalVisible}
        onCancel={() => {
          setSendEmailModalVisible(false);
          sendForm.resetFields();
        }}
        footer={null}
        width={600}
        style={{ top: 20 }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form
          form={sendForm}
          layout="vertical"
          onFinish={handleSendEmail}
        >
          <Form.Item
            name="recipientEmail"
            label="Recipient Merchant Email"
            rules={[
              { required: true, message: 'Please select or enter recipient email' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Select
              showSearch
              placeholder="Select merchant or enter email"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={merchants.map((m: any) => ({
                label: `${m.firstName} ${m.lastName} (${m.email})`,
                value: m.email,
              }))}
              loading={loadingMerchants}
              notFoundContent={loadingMerchants ? <Text type="secondary">Loading merchants...</Text> : null}
              allowClear
            />
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter subject' }]}
          >
            <Input placeholder="Email subject" />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter message' }]}
          >
            <TextArea rows={6} placeholder="Email message content" />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: '12px' }}>
            Note: This email will also be sent as a critical notification to the recipient.
          </Text>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={sending}>
                Send Email
              </Button>
              <Button onClick={() => {
                setSendEmailModalVisible(false);
                sendForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default MerchantInternalEmails;

